import React from 'react';
import { Button } from '@material-ui/core';
import { Control, useForm, DeepMap, FieldError } from 'react-hook-form';
import TranslatedText from '../../../../components/atoms/translated_text';
import {
  AppDatePicker,
  AppTextField,
} from '../../../../components/molecules/form_controls';
import GridLayout from '../../../../components/molecules/grid_layout';
import { Translations } from '../../configuration';
import { useHistory } from 'react-router';
import NavigationConfig from 'navigation/config';
import * as DateUtils from 'utils/dates';
import useQuery from 'custom_hooks/useQuery';

interface FormFields {
  vatNumber: string | null;
  vatReturnReference: string | null;
  remindedDateFrom: Date | null;
  remindedDateTo: Date | null;
}

interface IProps {
  vatNumber: string | null;
  vatReturnReference: string | null;
  remindedDateFrom: Date | null;
  remindedDateTo: Date | null;
}

const FilterForm = (props: IProps) => {
  const history = useHistory();
  const queryParams = useQuery();
  const defaultValues = {
    vatNumber: props.vatNumber ? props.vatNumber : null,
    vatReturnReference: props.vatReturnReference
      ? props.vatReturnReference
      : null,
    remindedDateFrom: props.remindedDateFrom ? props.remindedDateFrom : null,
    remindedDateTo: props.remindedDateTo ? props.remindedDateTo : null,
  };
  const { control, errors, handleSubmit, reset } = useForm<FormFields>({
    defaultValues,
  });

  React.useEffect(() => {
    reset(defaultValues);
  }, [
    props.vatNumber,
    props.vatReturnReference,
    props.remindedDateFrom,
    props.remindedDateTo,
    reset,
  ]);

  const onSubmit = (data: FormFields) => {
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('vatNumber');
    nextParams.delete('vatReturnReference');
    nextParams.delete('remindedDateFrom');
    nextParams.delete('remindedDateTo');
    nextParams.delete('sortField');
    nextParams.delete('page');
    nextParams.delete('pageSize');
    nextParams.delete('order');

    if (data.vatNumber) nextParams.set('vatNumber', data.vatNumber);
    if (data.vatReturnReference)
      nextParams.set('vatReturnReference', data.vatReturnReference);
    if (data.remindedDateFrom)
      nextParams.set(
        'remindedDateFrom',
        DateUtils.DateInQueryFormat(data.remindedDateFrom)
      );
    if (data.remindedDateTo)
      nextParams.set(
        'remindedDateTo',
        DateUtils.DateInQueryFormat(data.remindedDateTo)
      );

    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.paymentRemindersMsidPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  const clearFilter = () => {
    reset({
      vatNumber: null,
      vatReturnReference: null,
      remindedDateFrom: null,
      remindedDateTo: null,
    });
    const nextParams = new URLSearchParams(queryParams.toString());
    nextParams.delete('vatNumber');
    nextParams.delete('vatReturnReference');
    nextParams.delete('remindedDateFrom');
    nextParams.delete('remindedDateTo');
    nextParams.delete('sortField');
    nextParams.delete('page');
    nextParams.delete('pageSize');
    nextParams.delete('order');
    const queryString = nextParams.toString();
    history.push(
      NavigationConfig.paymentRemindersMsidPage().path +
        (queryString ? `?${queryString}` : '')
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} style={{ marginBottom: '0.6rem' }}>
      <GridLayout
        elements={
          [
            {
              element: renderVatNumberField(errors, control),
              size: 4,
              id: 'vatNumber',
            },
            {
              element: renderVatReturnReferenceField(errors, control),
              size: 4,
              id: 'vatReturnReference',
            },
            {
              element: renderRemindedDateFromField(errors, control),
              size: 2,
              id: 'remindedDateFrom',
            },
            {
              element: renderRemindedDateToField(errors, control),
              size: 2,
              id: 'remindedDateTo',
            },
            {
              element: (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginRight: '0.25rem',
                    }}
                  >
                    <TranslatedText textMap={Translations.search} />
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      width: '47%',
                      marginTop: '0.6rem',
                      marginLeft: '0.25rem',
                    }}
                    onClick={clearFilter}
                  >
                    <TranslatedText textMap={Translations.clear} />
                  </Button>
                </div>
              ),
              size: 12,
              id: 'actions',
            },
          ] as any
        }
      />
    </form>
  );
};

const renderVatNumberField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => (
  <AppTextField
    variant="outlined"
    fullWidth
    id="vatNumber"
    label={<TranslatedText textMap={Translations.vat_number_search_control} />}
    error={'vatNumber' in errors}
    name="vatNumber"
    control={control}
    rules={{}}
    helperText={errors.vatNumber && errors.vatNumber.message}
  />
);

const renderVatReturnReferenceField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => (
  <AppTextField
    variant="outlined"
    fullWidth
    id="vatReturnReference"
    label={
      <TranslatedText textMap={Translations.vat_return_reference_search_control} />
    }
    error={'vatReturnReference' in errors}
    name="vatReturnReference"
    control={control}
    rules={{}}
    helperText={
      errors.vatReturnReference && errors.vatReturnReference.message
    }
  />
);

const renderRemindedDateFromField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => (
  <AppDatePicker
    fullWidth
    id="remindedDateFrom"
    label={<TranslatedText textMap={Translations.reminded_date_from} />}
    error={'remindedDateFrom' in errors}
    name="remindedDateFrom"
    control={control}
    rules={{}}
    autoOk
  />
);

const renderRemindedDateToField = (
  errors: DeepMap<FormFields, FieldError>,
  control: Control<FormFields>
) => (
  <AppDatePicker
    fullWidth
    id="remindedDateTo"
    label={<TranslatedText textMap={Translations.reminded_date_to} />}
    error={'remindedDateTo' in errors}
    name="remindedDateTo"
    control={control}
    rules={{}}
    autoOk
  />
);

export default FilterForm;
